import { useEffect, useRef } from "react";
import _useSWR, { useSWRConfig, SWRConfiguration, SWRResponse } from "swr";

import { loadToken } from "src/helpers/auth";
import { laggyMiddleware } from "./middlewares";
import request from "src/api/request-manager";
import requestGateway from "src/api/request-manager/gateway";
import requestSandbox from "src/api/request-manager/sandbox";
import requestPWF from "src/api/request-manager/pwf";
import requestAPGateway from "src/api/request-manager/ap-gateway";
import requestDISDev from "src/api/request-manager/dis-dev";

/**
 * Cache revalidate frequency enums
 * 0 -> for pages that data changed on interval
 * 1 -> for pages that often changed
 * 2 -> for pages that not need much update
 * 3 -> for pages that rarely updated [default]
 */
enum FREQUENCY {
  INTERVAL,
  OFTEN,
  NORMAL,
  RARE
}

/**
 * Request manager type that will be sued to fetch data
 */
enum REQUEST_TYPE {
  LIVE = "live",
  SANDBOX = "sandbox",
  PWF = "pwf",
  GATEWAY = "gateway",
  APGATEWAY = "apgateway",
  DISDEV = "disdev"
}

const requestManager = {
  [REQUEST_TYPE.LIVE]: request,
  [REQUEST_TYPE.SANDBOX]: requestSandbox,
  [REQUEST_TYPE.PWF]: requestPWF,
  [REQUEST_TYPE.GATEWAY]: requestGateway,
  [REQUEST_TYPE.APGATEWAY]: requestAPGateway,
  [REQUEST_TYPE.DISDEV]: requestDISDev
};

const DEFAULT_THROTTLE_TIME = 20000;
const DEFAULT_DEDUPE_TIME = 15000;
const DEFAULT_REFRESH_INTERVAL_TIME = 10000;

/**
 * Fetcher for live or sandbox endpoint
 *
 * @param {string} type ["live" | "sandbox"] fetcher type
 * @param  {...any} args
 */
const fetcher = (type: REQUEST_TYPE, ...args: any[]) => {
  const fetchHandler = requestManager[type];
  const [urls, ...restArgs] = args;
  if (Array.isArray(urls)) {
    return Promise.all(urls.map((url) => fetchHandler.get(url, ...restArgs)));
  }
  // @ts-ignore
  return fetchHandler.get(...args);
};

export type UseSWROptions<Data> = {
  endpoint?: string | string[];
  params?: string | Object;
  options?: SWRConfiguration;
  type?: REQUEST_TYPE;
  headers?: Object;
  frequency?: FREQUENCY;
  initialData?: Data;
  interval?: number;
  callback: (data: SWRResponse<Data>) => void;
  laggy?: boolean;
};

type SWRResponseWithLaggyData<Data> = SWRResponse<Data> & { laggyData: Data };
export type SWRResponseType<Data> = SWRResponseWithLaggyData<Data> & { loading: boolean; isValidating: boolean };

/**
 * Hooks to do fetching GET endpoint with cache, using swr module
 *
 * @param {string} endpoint -> endpoint to fetch
 * @param {string} params -> params needed to send with fetch
 * @param {object} options -> additional options needed for custom case, see available options here https://swr.vercel.app/docs/options
 * @param {string} type -> ["live" | "sandbox"] determine type of request manager will be used
 * @param {object} headers -> custom headers to include
 * @param {number} frequency -> [0 | 1 | 2 | 3] revalidate frequency needed by the page
 * @param {any} initialData -> initial data
 * @param {number} interval -> number of interval of revalidate in miliseconds
 * @param {function} callback -> callback function to be called each render
 * @param {boolean} laggy -> shall swr use laggy middleware to persist fetched data between key changes
 */
function useSWR<Data extends any>({
  endpoint,
  params,
  options = {} as SWRConfiguration,
  type = REQUEST_TYPE.LIVE,
  headers,
  frequency = FREQUENCY.RARE,
  initialData,
  interval,
  callback,
  laggy
}: Partial<UseSWROptions<Data>> = {}) {
  const callbackRef = useRef<boolean>();
  const token = loadToken();

  // middlewares to use on every request
  const middlewares = [laggy && laggyMiddleware, options.use].flat().filter(Boolean);

  // default options to achieve frequency behavior
  const defaultOptions = {
    errorRetryCount: 1,
    focusThrottleInterval: DEFAULT_THROTTLE_TIME,
    revalidateIfStale: false,
    revalidateOnFocus: [FREQUENCY.NORMAL, FREQUENCY.OFTEN].includes(frequency),
    revalidateOnReconnect: true,
    refreshInterval: frequency === FREQUENCY.INTERVAL ? interval || DEFAULT_REFRESH_INTERVAL_TIME : 0,
    dedupingInterval: DEFAULT_DEDUPE_TIME
  };

  // useSWR required arguments
  const key = endpoint ? [endpoint, params, headers, token?.slice(-12)] : null;
  const typedFetcher = (...args: any[]) => fetcher(type, ...args);
  const config = { ...defaultOptions, ...options, use: middlewares };

  // where the fetching happened
  const swr = _useSWR(key, typedFetcher, config) as SWRResponseWithLaggyData<Data>;

  const { data, error, laggyData, mutate, isValidating: validating } = swr;

  // manual revalidate on mount, because revalidateIfStale & revalidateOnMount behavior not really good!
  useEffect(() => {
    if (data && frequency === FREQUENCY.OFTEN) {
      mutate();
    }
  }, []);

  // callback for every render after data exist
  useEffect(() => {
    if (data && callback) {
      callback(swr);

      // make sure callback finished first before loading state change
      // this to prevent react state update in unmounted component errors
      callbackRef.current = true;
    }
  }, [data]);

  const loading = endpoint && ((!data && !error) || (callback && !callbackRef.current));
  const isValidating = !loading && validating;

  return {
    ...swr,
    data: data || laggyData || initialData,
    loading,
    isValidating
  };
}

/**
 * hooks to clear cahce of specific key
 * to clear all cache existed, just put empty string as argument
 *
 * @param {string} key | key of cached endpoint to clear
 */
function useClearCache(key: string) {
  const { cache } = useSWRConfig();

  return () => {
    const targetKey: string[] = [];

    // @ts-ignore
    for (const [cacheKey] of cache) {
      const hasMatchingKey = cacheKey.includes(key);
      if (hasMatchingKey) {
        targetKey.push(cacheKey);
      }
    }

    if (targetKey.length > 0) {
      targetKey.forEach((k) => cache.delete(k));
    }
  };
}

export { useSWRConfig, useSWR, FREQUENCY, REQUEST_TYPE, useClearCache };
